import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

export function Modal({ open, onClose, title, size = "md", children, testId }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={onClose} data-testid={testId}>
      <div className={`modal ${size}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose} data-testid="modal-close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Badge({ children, color = "gray" }) {
  return <span className={`badge badge-${color}`}>{children}</span>;
}

export function Empty({ icon = "📂", title = "Sin registros", sub = "Crea el primero con el botón superior." }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <div className="font-semibold">{title}</div>
      <div className="text-sm">{sub}</div>
    </div>
  );
}

export function FileUploader({ module, parentId, onUploaded }) {
  const inputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [active, setActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);

  async function upload(files) {
    if (!files || !files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const form = new FormData();
      form.append("file", f);
      form.append("module", module);
      if (parentId) form.append("parent_id", parentId);
      // Preserve relative path when folder upload (webkitdirectory) is used
      const rel = f.webkitRelativePath || "";
      if (rel) form.append("relative_path", rel);
      try {
        const { data } = await api.post("/files/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (onUploaded) onUploaded(data);
      } catch (e) {
        console.error(e);
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    setProgress(null);
  }

  return (
    <div data-testid="file-uploader-wrap">
      <div
        className={`dropzone ${active ? "active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault(); setActive(false);
          upload(e.dataTransfer.files);
        }}
        data-testid="file-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files)}
          data-testid="file-input"
        />
        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          style={{ display: "none" }}
          onChange={(e) => upload(e.target.files)}
          data-testid="folder-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2">
            <span className="spinner" /> Subiendo {progress ? `${progress.done}/${progress.total}` : "..."}
          </div>
        ) : (
          <div>
            <div className="text-lg">Arrastra archivos o haz clic para subir</div>
            <div className="text-xs text-dim mt-2">PDF, imágenes, Excel, Word, PPT — selección múltiple</div>
          </div>
        )}
      </div>
      {!uploading && (
        <div className="flex gap-2 mt-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm"
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            data-testid="upload-files-btn"
          >
            Subir archivos
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
            data-testid="upload-folder-btn"
            title="Sube una carpeta completa preservando la estructura"
          >
            Subir carpeta completa
          </button>
          <span className="text-xs text-dim self-center">Soporta carpetas con subcarpetas (estructura preservada)</span>
        </div>
      )}
    </div>
  );
}

export function FileList({ module, parentId, refreshKey = 0 }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/files/list", { params: { module, parent_id: parentId || "" } });
      setFiles(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [module, parentId, refreshKey]);

  async function del(id) {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    await api.delete(`/files/${id}`);
    load();
  }

  if (loading) return <div className="text-dim text-sm">Cargando archivos...</div>;
  if (!files.length) return <div className="text-dim text-sm">Sin archivos cargados.</div>;

  // Group by folder for display when folder uploads were used
  const groups = {};
  files.forEach((f) => {
    const key = f.folder || "__root__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  });
  const folderKeys = Object.keys(groups).sort((a, b) => {
    if (a === "__root__") return -1;
    if (b === "__root__") return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="grid gap-3" data-testid="file-list">
      {folderKeys.map((fk) => (
        <div key={fk}>
          {fk !== "__root__" && (
            <div className="text-xs font-semibold mt-1 mb-1" style={{ color: "var(--accent)", textTransform: "uppercase", letterSpacing: 1 }}>
              📁 {fk}
            </div>
          )}
          <div className="grid gap-2">
            {groups[fk].map((f) => (
              <div key={f.id} className="card" style={{ padding: 10 }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    <div className="font-semibold text-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name || f.original_name}
                    </div>
                    <div className="text-xs text-dim mt-2">
                      {(f.size / 1024).toFixed(1)} KB · subido por {f.uploaded_by_name} · {new Date(f.created_at).toLocaleDateString("es-MX")}
                      {f.relative_path && <span> · <code style={{ fontSize: 10 }}>{f.relative_path}</code></span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a className="btn btn-sm" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/view/${f.id}`} target="_blank" rel="noreferrer" data-testid={`file-view-${f.id}`}>Ver</a>
                    <a className="btn btn-sm" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${f.id}`} download data-testid={`file-download-${f.id}`}>Descargar</a>
                    <button className="btn btn-sm btn-danger" onClick={() => del(f.id)} data-testid={`file-delete-${f.id}`}>×</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FileCCE({ module, parentId, onFileUploaded }) {
  const [refresh, setRefresh] = useState(0);
  return (
    <div className="grid gap-3">
      <FileUploader module={module} parentId={parentId} onUploaded={(f) => { setRefresh((r) => r + 1); if (onFileUploaded) onFileUploaded(f); }} />
      <FileList module={module} parentId={parentId} refreshKey={refresh} />
    </div>
  );
}

export function StatusBadge({ value }) {
  const map = {
    "Cumple": "green", "Vigente": "green", "Aprobado": "green", "Cerrada": "green", "Pagado": "green", "Activo": "green",
    "Parcial": "yellow", "En proceso": "yellow", "En revisión": "yellow", "En atención": "yellow", "Pendiente": "yellow", "Por vencer": "yellow",
    "No cumple": "red", "Vencido": "red", "Abierta": "red", "Vencida": "red", "Suspendido": "red", "Crítico": "red", "Rechazado": "red",
    "No evaluado": "gray",
    "Programada": "blue", "Notificada": "blue",
  };
  return <Badge color={map[value] || "gray"}>{value || "-"}</Badge>;
}

export function exportTableToCSV(rows, filename = "export.csv") {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).filter((k) => k !== "_id");
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => {
      const v = r[h];
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[,\n"]/.test(s) ? `"${s}"` : s;
    }).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
